import React, { useState } from 'react';
import { invoiceApi } from '../api';
import { Card, Button } from '../components/UI';
import { toast } from 'react-toastify';
import { Download } from 'lucide-react';

const AnnualAuditPage = () => {
    const [selectedYear, setSelectedYear] = useState('');
    const [years, setYears] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchingYears, setFetchingYears] = useState(true);

    React.useEffect(() => {
        const fetchYears = async () => {
            try {
                const response = await invoiceApi.getAuditYears();
                const availableYears = response.data || [];
                setYears(availableYears);
                if (availableYears.length > 0) {
                    setSelectedYear(availableYears[0]);
                } else {
                    // Fallback if no years found, maybe current financial year
                    const currentYear = new Date().getFullYear();
                    const currentMonth = new Date().getMonth(); // 0-11
                    const fyStart = currentMonth >= 3 ? currentYear : currentYear - 1;
                    setYears([fyStart]);
                    setSelectedYear(fyStart);
                }
            } catch (error) {
                console.error('Failed to fetch audit years:', error);
                toast.error('Failed to load financial years. Using default.');

                // Fallback on error
                const currentYear = new Date().getFullYear();
                const currentMonth = new Date().getMonth(); // 0-11
                const fyStart = currentMonth >= 3 ? currentYear : currentYear - 1;
                setYears([fyStart]);
                setSelectedYear(fyStart);
            } finally {
                setFetchingYears(false);
            }
        };

        fetchYears();
    }, []);

    const handleDownload = async () => {
        setLoading(true);
        try {
            const response = await invoiceApi.downloadAuditZip(selectedYear);

            // Create a blob from the response data
            const blob = new Blob([response.data], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Processed_Invoices_FY_${selectedYear}-${selectedYear + 1}.zip`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            toast.success('Audit ZIP downloaded successfully');
        } catch (error) {
            console.error('Error downloading audit zip:', error);
            toast.error('Failed to download audit ZIP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-6 max-w-4xl mx-auto">
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900">Annual Audit Download</h1>
                <p className="mt-2 text-gray-600">
                    Download all processed invoices for a specific financial year, organized by Vendor and Month.
                </p>
            </div>

            <Card>
                <div className="border-b border-gray-200 pb-4 mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Download Configuration</h2>
                </div>
                <div>
                    <div className="flex flex-col sm:flex-row items-end gap-4">
                        <div className="w-full sm:w-64">
                            <label htmlFor="year-select" className="block text-sm font-medium text-gray-700 mb-2">
                                Select Financial Year
                            </label>
                            <select
                                id="year-select"
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm p-2.5 border"
                                disabled={fetchingYears}
                            >
                                {fetchingYears ? (
                                    <option>Loading years...</option>
                                ) : years.length === 0 ? (
                                    <option disabled>No data available</option>
                                ) : (
                                    years.map((year) => (
                                        <option key={year} value={year}>
                                            FY {year}-{year + 1}
                                        </option>
                                    ))
                                )}
                            </select>
                        </div>

                        <Button
                            onClick={handleDownload}
                            loading={loading}
                            disabled={fetchingYears || loading}
                            icon={Download}
                            variant="primary"
                        >
                            Download Audit ZIP
                        </Button>
                    </div>

                    <div className="mt-6 rounded-md bg-blue-50 p-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3 flex-1 md:flex md:justify-between">
                                <p className="text-sm text-blue-700">
                                    The download process may take a few minutes depending on the number of invoices.
                                    The ZIP file will contain folders structured as: <strong>FY {selectedYear}-{selectedYear + 1} / Vendor Name / Month / Invoice.pdf</strong>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default AnnualAuditPage;
